import { schema } from '../schema'
import { addMockFunctionsToSchema } from 'graphql-tools'
import { mockApplications } from './mock-applications'
import { mockServers } from './mock-servers'
import { mockVulnerabilities } from './mock-vulnerabilities'
import { Language } from './shared'
import { mockLibraries } from './mock-libraries'
import { mockAttackEvents } from './mock-attack-events'
import { AgentLanguage } from '../../../components/agent-language'
import { getMockLibraryUsageItems } from '../queries/mock-library-usage'

const range = (n) => Array.from(Array(n).keys())

export const getRand = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min

interface MockPageResolverProps {
  readonly total?: number
  readonly pageSize?: number
  readonly delay?: number
  readonly mockItems: any[]
}

const simulateLoading = (items, total) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        items,
        total
      })
    }, 250)
  })
}

function getMockPageResolver({
  total = 200,
  pageSize = 25,
  delay = 250,
  mockItems
}: MockPageResolverProps) {
  let offset = 0

  if (mockItems.length < 1) {
    throw new Error('mockItems must have at least one item')
  }
  if (total < 1) {
    throw new Error('total must be greater than one')
  }
  if (pageSize < 1) {
    throw new Error('pageSize must be greater than one')
  }
  if (delay < 1) {
    throw new Error('delay must be greater than one')
  }
  return () => {
    const items: any[] = []
    const itemCount = Math.min(total - offset, pageSize)

    while (items.length < itemCount) {
      const mockItem = mockItems[offset++ % mockItems.length]

      items.push({
        ...mockItem,
        id: !mockItem.id || mockItem.id === 'id' ? offset : mockItem.id
      })
    }

    return simulateLoading(items, total)
  }
}

const mocks = {
  ApplicationPage: getMockPageResolver({ mockItems: mockApplications }),
  ServerPage: getMockPageResolver({ mockItems: mockServers }),
  VulnerabilityPage: getMockPageResolver({
    mockItems: mockVulnerabilities
  }),
  LibraryUsage: () => {
    const totalUsed = getRand(0, 1000)
    const total = totalUsed + getRand(1000, 2000)
    return {
      totalUsed,
      total
    }
  },
  LibraryUsagePage: () => {
    const items = getMockLibraryUsageItems('1', 'abc123')
    return simulateLoading(items, items.length)
  },
  LibraryPage: () => simulateLoading(mockLibraries, mockLibraries.length),
  AttackEventsPage: () =>
    simulateLoading(mockAttackEvents, mockAttackEvents.length),
  Application: () => {
    // Generate a long random number so we can test truncating
    const n = getRand(0, 100000)
    return {
      name: `Mock Application ${n}`,
      language: AgentLanguage.JAVA.getName()
    }
  },
  ApplicationAggregation: () => {
    return {
      compliance: range(10).map((i) => {
        return {
          count: 1,
          key: {
            id: i + '',
            name: `compliance-name-${i}`
          }
        }
      }),
      servers: (() => {
        let id = 0
        let list = range(2).map(() => {
          id++
          return {
            count: 1,
            key: {
              hostname: `server-host-${id}`,
              id: id + '',
              path: `server-path-${id}`
            }
          }
        })
        id++
        list = list.concat([
          {
            count: 999,
            key: {
              hostname: `server-host-${id}`,
              id: id + '',
              path: `server-path-${id}-longlonglonglonglonglonglonglonglonglonglonglonglonglong`
            }
          }
        ])
        return list
      })(),
      languages: [
        { count: 0, key: Language.JAVA },
        { count: 1, key: Language.NODE },
        { count: 10, key: Language.DOT_NET },
        { count: 100, key: Language.PYTHON },
        { count: 1000, key: Language.RUBY }
      ],
      tags: range(100).map((i) => {
        return {
          count: 1,
          key: `tag-${i}`
        }
      }),
      // Keep last category empty
      technologies: []
    }
  },
  Query: () => {
    return {
      applicationGrade: {
        score: 100,
        value: 'A'
      },
      applicationModules: [
        { id: 'module1', name: 'Child App 1' },
        { id: 'module2', name: 'Child App 2' }
      ],
      applicationQuickFilters: [
        {
          name: 'All',
          count: 100,
          id: 'ALL'
        },
        {
          name: 'Online',
          count: 48,
          id: 'ONLINE'
        },
        {
          name: 'Offline',
          count: 52,
          id: 'OFFLINE'
        },
        {
          name: 'Merged',
          count: 1,
          id: 'MERGED'
        },
        {
          name: 'Licensed',
          count: 51,
          id: 'LICENSED'
        },
        {
          name: 'Unlicensed',
          count: 49,
          id: 'UNLICENSED'
        },
        {
          name: 'High Risk',
          count: 52,
          id: 'HIGH_RISK'
        },
        {
          name: 'Incomplete Protection',
          count: 0,
          id: 'INCOMPLETE_PROTECTION'
        },
        {
          name: 'Archived',
          count: 1,
          id: 'ARCHIVED'
        }
      ],
      customApplicationAggregations: [
        {
          id: 'poc-id-1',
          fieldType: 'PERSON_OF_CONTACT',
          label: 'Lead Engineer',
          buckets: [
            {
              count: 2,
              key: {
                id: 'id-1',
                value: 'John Smith',
                subfieldValues: [
                  {
                    fieldType: 'CONTACT_NAME',
                    value: 'John Smith'
                  },
                  {
                    fieldType: 'CONTACT_PHONE',
                    value: '1112223333'
                  },
                  {
                    fieldType: 'CONTACT_EMAIL',
                    value: 'John.Smith@aol.com'
                  }
                ]
              }
            },
            {
              count: 5,
              key: {
                id: 'id-2',
                value: 'John Smith Jr.',
                subfieldValues: [
                  {
                    fieldType: 'CONTACT_NAME',
                    value: 'John Smith'
                  },
                  {
                    fieldType: 'CONTACT_PHONE',
                    value: '1112223334'
                  },
                  {
                    fieldType: 'CONTACT_EMAIL',
                    value: 'John.Smith.Jr@aol.com'
                  }
                ]
              }
            }
          ]
        }
      ],
      viewer: {
        id: 'current_user',
        firstName: 'John',
        lastName: 'Doe',
        protect: true
      }
    }
  },
  AgentFeatures: () => {
    return {
      features: [
        {
          name: 'assess',
          languages: ['JAVA', 'DOTNET', 'NODE']
        },
        {
          name: 'protection',
          languages: ['JAVA', 'DOTNET', 'NODE']
        }
      ]
    }
  },
  Organization: () => {
    return {
      id: '1',
      type: 'Standard',
      role: 'ADMIN',
      assessLicenseBreakdown: {
        unused: 100,
        used: 100
      },
      protectLicenseBreakdown: {
        unused: 100,
        used: 100
      }
    }
  },
  ServerAggregation: () => {
    return {
      tags: [],
      agentVersions: [
        {
          count: 13,
          key: '3.5.5'
        }
      ],
      environments: [
        {
          count: 3,
          key: 'DEVELOPMENT'
        },
        {
          count: 62,
          key: 'QA'
        },
        {
          count: 12,
          key: 'PRODUCTION'
        }
      ],
      logLevels: [
        {
          count: 20,
          key: 'ERROR'
        }
      ],
      applications: [
        {
          count: 20,
          key: {
            id: 'abc-123',
            name: 'ABC Mouse'
          }
        }
      ]
    }
  },
  DateTime: () => '1970-01-01T00:00:00.000Z'
}

export function getMockSchema() {
  addMockFunctionsToSchema({ schema, mocks })
  return schema
}
